import { useEffect, useRef, useState, type ComponentType, type ReactElement, type RefObject } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * PRZYCISK Z MINI LISTĄ WYBORU — pobieranie startuje PO wyborze, nie przed.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ WYDZIELONE Z `PrzyciskTxt`, KTÓRY SAM O TO PROSIŁ SWOIM KOMENTARZEM („jeden komponent na oba
 *  miejsca; dwie kopie znaczyłyby, że jedna dostanie kiedyś poprawkę, a druga zostanie").
 *  Obsługa listy nie jest trywialna i to ona jest tu treścią: `pointerdown` na dokumencie zamiast
 *  `onBlur`, Escape oddający fokus przyciskowi, fokus na pierwszej pozycji po otwarciu klawiaturą,
 *  zamknięcie listy, gdy przycisk zostanie zablokowany w trakcie pobierania.
 *
 *  Drugim odbiorcą jest mapa planogramu (kolor po towarze / po grupie) — ta sama zasada: pytamy,
 *  bo jest kto odpowiedzieć, i dopiero odpowiedź uruchamia pobranie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type PozycjaWyboru<K extends string> = {
  klucz: K;
  etykieta: string;
  /** Druga linia pozycji — po co to komu. Pusto zostawiamy, gdy etykieta mówi wszystko. */
  opis?: string;
};

export function PrzyciskZWyborem<K extends string>({
  etykieta,
  ikona: Ikona,
  pozycje,
  onWybor,
  ariaLabel,
  disabled = false,
  pracuje = false,
  wGore = false,
  size = 'sm',
  tytul,
  dataExport,
}: {
  etykieta: string;
  ikona: ComponentType<{ className?: string }>;
  pozycje: PozycjaWyboru<K>[];
  /** Wywoływane PO wyborze — dopiero to zaczyna pobieranie. */
  onWybor: (klucz: K) => void;
  /** Nazwa listy dla czytnika ekranu; bez niej wchodzi etykieta przycisku. */
  ariaLabel?: string;
  disabled?: boolean;
  /** Trwa pobieranie tego formatu — kręciołek zamiast ikony. */
  pracuje?: boolean;
  /**
   * Lista rozwija się DO GÓRY.
   *
   * ⚠️ POTRZEBNE W STOPCE MODALA. Stopka jest `sticky` na dole przewijanego okna, więc lista
   * otwarta w dół wychodzi poza `overflow-y-auto` kontenera i zostaje ucięta.
   */
  wGore?: boolean;
  size?: 'sm' | 'default';
  tytul?: string;
  dataExport?: string;
}) {
  const [otwarte, setOtwarte] = useState(false);
  const kotwica = useRef<HTMLDivElement>(null);
  const przycisk = useRef<HTMLButtonElement>(null);
  const pierwsza = useRef<HTMLButtonElement>(null);

  /** Zablokowanie przycisku w trakcie eksportu nie może zostawić otwartej listy nad niczym. */
  useEffect(() => {
    if (disabled || pracuje) setOtwarte(false);
  }, [disabled, pracuje]);

  useEffect(() => {
    if (!otwarte) return;
    /**
     * ⚠️ `pointerdown` NA DOKUMENCIE, a nie `onBlur` na przycisku. Fokus wchodzi do pozycji listy,
     * więc `onBlur` zamykałby ją dokładnie w chwili, w której człowiek próbuje kliknąć.
     */
    const naZewnatrz = (e: PointerEvent): void => {
      if (kotwica.current !== null && !kotwica.current.contains(e.target as Node)) setOtwarte(false);
    };
    const naKlawiszu = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setOtwarte(false);
      /** Escape oddaje fokus przyciskowi — inaczej klawiatura zostaje bez punktu zaczepienia. */
      przycisk.current?.focus();
    };
    document.addEventListener('pointerdown', naZewnatrz);
    document.addEventListener('keydown', naKlawiszu);
    /** Fokus na pierwszej pozycji: lista otwarta klawiaturą ma być od razu obsługiwalna. */
    pierwsza.current?.focus();
    return () => {
      document.removeEventListener('pointerdown', naZewnatrz);
      document.removeEventListener('keydown', naKlawiszu);
    };
  }, [otwarte]);

  const wybierz = (klucz: K): void => {
    setOtwarte(false);
    onWybor(klucz);
  };

  const pozycja = (p: PozycjaWyboru<K>, ref?: RefObject<HTMLButtonElement | null>): ReactElement => (
    <button
      key={p.klucz}
      ref={ref}
      type="button"
      role="menuitem"
      className="block w-full px-2.5 py-1.5 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
      onClick={() => wybierz(p.klucz)}
    >
      <span className="block text-xs font-medium">{p.etykieta}</span>
      {p.opis !== undefined && (
        <span className="block text-[11px] leading-snug text-muted-foreground">{p.opis}</span>
      )}
    </button>
  );

  return (
    <div className="relative" ref={kotwica}>
      <Button
        ref={przycisk}
        type="button"
        variant="outline"
        size={size}
        disabled={disabled}
        onClick={() => setOtwarte((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={otwarte}
        data-export={dataExport}
        title={tytul}
      >
        {pracuje ? <Loader2 className="animate-spin" /> : <Ikona />}
        {etykieta}
        <ChevronDown className="size-3 opacity-60" />
      </Button>

      {otwarte && (
        <div
          role="menu"
          aria-label={ariaLabel ?? etykieta}
          className={cn(
            'absolute right-0 z-30 min-w-52 overflow-hidden rounded-md border border-border bg-[var(--popover,var(--card))] shadow-md',
            wGore ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {pozycje.map((p, i) => pozycja(p, i === 0 ? pierwsza : undefined))}
        </div>
      )}
    </div>
  );
}
